import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HierarchyFilterComponent } from '../../../components/FilterBar/HierarchyFilter';
import { MetricBox, MachineStatusBox } from '../../../components/MetricBox';
import { HierarchyFilter, OperationalPerformanceData } from '../../../lib/types';
import { subDays } from 'date-fns';

export function OperationalPerformance() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [filter, setFilter] = useState<HierarchyFilter>({
    dateFrom: subDays(new Date(), 30).toISOString(),
    dateTo: new Date().toISOString(),
    granularity: 'day',
    compareToPrevious: false,
    brandScope: 'sku_or_campaign',
  });

  const [data, setData] = useState<OperationalPerformanceData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        setUser(data.user);
      });
    }
  }, []);

  useEffect(() => {
    if (user?.tenantId) {
      fetchOperationalData();
    }
  }, [user, filter]);

  const fetchOperationalData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        dateFrom: filter.dateFrom,
        dateTo: filter.dateTo,
        granularity: filter.granularity,
        compareToPrevious: String(filter.compareToPrevious || false),
        brandScope: filter.brandScope || 'sku_or_campaign',
      });

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/brand/${user.tenantId}/dashboard/operational?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch operational data');
      }

      const data = await response.json();
      setData(data);
    } catch (error) {
      console.error('Error fetching operational data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    console.log('Export CSV');
  };

  const handleExportPNG = () => {
    console.log('Export PNG');
  };

  const handleExportPagePDF = () => {
    console.log('Export Page PDF');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-red-600">Failed to load data</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Filter Section with proper separation */}
      <div className="bg-white shadow-sm">
        <HierarchyFilterComponent
          filter={filter}
          onChange={setFilter}
          onExportCSV={handleExportCSV}
          onExportPNG={handleExportPNG}
          onExportPagePDF={handleExportPagePDF}
          showBrandScope={true}
        />
      </div>

      {/* Main Content with proper spacing */}
      <div className="max-w-7xl mx-auto p-6 pt-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Operational Performance</h1>
          <p className="text-gray-600">Monitor machine status, vend performance, and stock levels</p>
        </div>

        {/* Machine Status Boxes */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MachineStatusBox title="All Machines" count={data.machineStatus.all} color="blue" />
          <MachineStatusBox title="Live Machines" count={data.machineStatus.live} color="green" />
          <MachineStatusBox title="Offline Machines" count={data.machineStatus.offline} color="yellow" />
          <MachineStatusBox title="Out-of-Service Machines" count={data.machineStatus.outOfService} color="red" />
        </div>

        {/* Vend Metrics Boxes */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MetricBox title="Successful Vends" value={data.vendMetrics.successful} format="number" />
          <MetricBox title="Failed Vends" value={data.vendMetrics.failed} format="number" />
          <MetricBox title="Empty Vends" value={data.vendMetrics.empty} format="number" />
          <MetricBox title="Failed Payments" value={data.vendMetrics.failedPayments} format="number" />
        </div>

        {/* Stock Level Tile */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <MetricBox 
            title="Total Stock Level" 
            value={data.totalStockLevel} 
            format="percentage" 
            decimals={1}
            className="lg:col-span-1"
          />
          <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Stock Level Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Average Stock Level</span>
                <span className="text-sm font-semibold text-gray-900">{data.totalStockLevel.value.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Trend vs Previous Period</span>
                <span className={`text-sm font-semibold ${
                  data.totalStockLevel.trend && data.totalStockLevel.trend > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {data.totalStockLevel.trend && data.totalStockLevel.trend > 0 ? '+' : ''}{data.totalStockLevel.trend?.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full" 
                  style={{ width: `${Math.min(data.totalStockLevel.value, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
